import { CopyOutlined } from "@ant-design/icons";
import { Form, Modal, Row, Col, DatePicker, Select, Input, InputNumber, Spin, Typography } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CHALLAN_LIST,
  GET_SUPPLIER_ENDPOINT,
  INVOICE_BY_SUPPLIER,
  RAW_MATERIAL_INVENTORY_EDIT,
  RAW_MATERIAL_UPDATE,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import HandleNotification from "../../../common/Notification";
import StoreRequisitionNoSelect from "common/StoreRequisitionNoSelect";
import { commaSeparateNumber } from "../../../utils/functions";

const { Text } = Typography;

const normalizeTrackRows = (data) => {
  const first = data?.track_item_histories;
  const second = data?.track_item_history;

  let list = [];
  if (Array.isArray(first) && first.length > 0) {
    list = first;
  } else if (Array.isArray(second) && second.length > 0) {
    list = second;
  } else if (first && !Array.isArray(first)) {
    list = [first];
  } else if (second && !Array.isArray(second)) {
    list = [second];
  }

  return list.map((th) => {
    const ti = th.track_item || {};
    const bin = ti.bins_info || {};
    const rack = bin.rack_info || {};
    const wh = rack.warehouse_info || {};
    const binId = ti.bin_id ?? bin?.id;
    return {
      history_id: th.id,
      track_id: th.track_id ?? ti.id,
      bin_id: binId,
      is_inside: !!binId,
      warehouse_label: wh.name || "—",
      rack_label: rack.name || "—",
      bin_label: bin.name || "—",
      total_balance: Number(th.total_quantity ?? ti.total_quantity ?? 0),
      // total_shipment is used as "Receive Qty" / "Issue Qty" (editable)
      total_shipment: Number(th.shipment_qty ?? ti.shipment_qty ?? 0),
      // shipment_qty_fixed is the shipment qty column value (outside locations keep this as-is)
      shipment_qty_fixed: Number(th.shipment_qty ?? ti.shipment_qty ?? 0),
      convertion_unit_id: th.convertion_unit_id ?? th.convertion_unit_info?.id ?? 1,
      convertion_rate: th.convertion_rate != null ? Number(th.convertion_rate) : 1,
    };
  });
};

const TrimsStoreEntryEditModal = ({ open, recordId, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [supplierList, setSupplierList] = useState([]);
  const [challanList, setChallanList] = useState([]);
  const [invoiceList, setInvoiceList] = useState([]);
  const [trackRows, setTrackRows] = useState([]);
  const tracksWatch = Form.useWatch("tracks", form);
  const entryDateWatch = Form.useWatch("entry_date", form);
  const movementType = detail?.movement_type || "";

  const incomingMovementTypes = [
    "receive_from_supplier",
    "receive_from_sub_con_factory",
    "return_from_cutting",
    "return_from_sewing",
    "return_from_folding",
    "return_from_packing",
    "assign_from_free_balance",
    "return_from_cutting_final",
    "return_from_buying_office",
    "return_from_cutting_loose",
    "return_from_cutting_final_loose",
  ];

  const outgoingMovementTypes = [
    "issue_to_cutting",
    "issue_to_sewing",
    "issue_to_folding",
    "issue_to_packing",
    "mark_as_free",
    "issue_to_sample",
    "send_to_buying_office",
    "issue_to_lab",
    "issue_to_cutting_final",
    "stock_sale",
    "return_to_supplier",
    "return_to_sub_con_factory",
    "disposal",
    "issue_to_others",
    "issue_to_cutting_loose",
    "issue_to_cutting_final_loose",
  ];

  const actionLabel = useMemo(() => {
    if (incomingMovementTypes.includes(movementType)) return "Receive";
    if (outgoingMovementTypes.includes(movementType)) return "Issue";
    // fallback (in case new movement types are added)
    return movementType?.includes("receive") ? "Receive" : "Issue";
  }, [movementType]);

  const issueQtyLabel = `${actionLabel} Qty`;

  const ensureSupplierInList = useCallback((data) => {
    const supplierId = Number(data?.supplier_id ?? data?.supplier_info?.id);
    const supplierName = data?.supplier_info?.name;
    if (!supplierId || !supplierName) return;

    setSupplierList((prev) => {
      const exists = prev.some((item) => Number(item?.id) === supplierId);
      if (exists) return prev;
      return [...prev, { id: supplierId, name: supplierName }];
    });
  }, []);

  const trackSums = useMemo(() => {
    const rows = Array.isArray(tracksWatch) ? tracksWatch : [];
    return rows.reduce(
      (acc, row) => {
        acc.totalBalance += Number(row?.total_balance ?? 0);
        const isInside = !!row?.is_inside;
        acc.totalShipment += Number(
          isInside ? row?.total_shipment ?? 0 : row?.shipment_qty_fixed ?? 0,
        );
        return acc;
      },
      { totalBalance: 0, totalShipment: 0 },
    );
  }, [tracksWatch]);

  const isReceiveFromSupplier = movementType === "receive_from_supplier";
  const isReceiveFromSubConFactory = movementType === "receive_from_sub_con_factory";
  const isReturnToSupplier = movementType === "return_to_supplier";
  const isReturnToSubConFactory = movementType === "return_to_sub_con_factory";
  const isIssueToLab = movementType === "issue_to_lab";
  const isSendToBuyingOffice = movementType === "send_to_buying_office";
  const isIssueToOthers = movementType === "issue_to_others";
  const isDisposal = movementType === "disposal";

  const incomingRequisitionRequiredTypes = [
    "return_from_cutting",
    "return_from_sewing",
    "return_from_folding",
    "return_from_finishing",
    "return_from_packing",
    "return_from_cutting_final",
    "return_from_cutting_loose",
    "return_from_cutting_final_loose",
  ];
  const outgoingRequisitionRequiredTypes = [
    "issue_to_cutting",
    "issue_to_sewing",
    "issue_to_folding",
    "issue_to_finishing",
    "issue_to_packing",
    "issue_to_sample",
    "issue_to_cutting_final",
    "issue_to_cutting_loose",
    "issue_to_cutting_final_loose",
  ];
  const showSupplier = isDisposal || isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier;
  const showInvoice = isDisposal || isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier;
  const showChallan = isDisposal || isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier;
  const showRequisition =
    isDisposal ||
    isIssueToLab ||
    isSendToBuyingOffice ||
    isIssueToOthers ||
    incomingRequisitionRequiredTypes.includes(movementType) ||
    outgoingRequisitionRequiredTypes.includes(movementType);
  const showReferenceNo = isIssueToLab || isSendToBuyingOffice || isIssueToOthers;
  const showGatePass = isSendToBuyingOffice || isReturnToSubConFactory;
  const requireRequisition =
    isIssueToLab ||
    isSendToBuyingOffice ||
    isIssueToOthers ||
    incomingRequisitionRequiredTypes.includes(movementType) ||
    outgoingRequisitionRequiredTypes.includes(movementType);
  const supplierFieldsDisabled = isReceiveFromSupplier && !entryDateWatch;

  const loadSuppliers = useCallback(async (name = "") => {
    const res = await getData(GET_SUPPLIER_ENDPOINT, false, { per_page: 100, name });
    if (res?.data?.code === 200) {
      setSupplierList(res?.data?.data?.data || []);
    }
  }, []);

  const loadChallans = useCallback(async (name = "") => {
    const res = await getData(CHALLAN_LIST, false, { per_page: 500, name });
    if (res?.data?.code === 200) {
      setChallanList(res?.data?.data?.data || []);
    }
  }, []);

  const loadInvoicesBySupplier = useCallback(async (supplierId) => {
    if (!supplierId) {
      setInvoiceList([]);
      return;
    }
    const res = await getData(`${INVOICE_BY_SUPPLIER}/${supplierId}`);
    if (res?.data?.code === 200) {
      setInvoiceList(res?.data?.data || []);
    }
  }, []);

  useEffect(() => {
    if (!open || !recordId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setDetail(null);
      setTrackRows([]);
      form.resetFields();

      try {
        const res = await getData(RAW_MATERIAL_INVENTORY_EDIT(recordId));
        if (cancelled) return;

        if (res?.data?.code !== 200) {
          HandleNotification("error", "bottomRight", res?.data?.message?.[0] || "Failed to load", null);
          onClose();
          return;
        }

        const data = res?.data?.data?.data || res?.data?.data;
        setDetail(data);
        const normalizedTracks = normalizeTrackRows(data);
        setTrackRows(normalizedTracks);

        form.setFieldsValue({
          entry_date: data.entry_date ? moment(data.entry_date, "YYYY-MM-DD") : undefined,
          supplier_id: data.supplier_id != null ? String(data.supplier_id) : undefined,
          invoice_id: data.invoice_id ?? undefined,
          challan_id: data.challan_id ?? undefined,
          requisition_no: data.requisition_no ?? undefined,
          reference_no: data.reference_no ?? undefined,
          gate_pass: data.gate_pass ?? undefined,
          received_total_quantity: Number(data.received_total_quantity) || 0,
          shipment_qty: Number(data.shipment_qty) || 0,
          shipment_unit: data?.convertion_unit?.name ?? data?.convertion_unit_info?.name ?? undefined,
          tracks: normalizedTracks,
        });

        await Promise.all([loadSuppliers(), loadChallans()]);
        ensureSupplierInList(data);
        if (data.supplier_id) {
          await loadInvoicesBySupplier(data.supplier_id);
        } else {
          setInvoiceList([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, recordId, form, loadSuppliers, loadChallans, loadInvoicesBySupplier, onClose, ensureSupplierInList]);

  useEffect(() => {
    if (!open) return;
    if (!Array.isArray(tracksWatch) || tracksWatch.length === 0) return;

    const currentReceived = Number(form.getFieldValue("received_total_quantity") ?? 0);
    const currentShipment = Number(form.getFieldValue("shipment_qty") ?? 0);
    const nextReceived = Number(trackSums.totalBalance.toFixed(2));
    const nextShipment = Number(trackSums.totalShipment.toFixed(2));

    if (currentReceived !== nextReceived || currentShipment !== nextShipment) {
      form.setFieldsValue({
        received_total_quantity: nextReceived,
        shipment_qty: nextShipment,
      });
    }
  }, [open, tracksWatch, trackSums, form]);

  const onSupplierChange = (supplierId) => {
    form.setFieldsValue({ invoice_id: undefined });
    loadInvoicesBySupplier(supplierId);
  };

  const syncTracks = (nextTracks) => {
    setTrackRows(nextTracks);
    form.setFieldsValue({ tracks: nextTracks });
  };

  const updateReceiveQty = (index, nextQty) => {
    const rows = form.getFieldValue("tracks") || [];
    const current = rows?.[index];
    if (!current) return;

    const receiveQty = Number(nextQty ?? 0);
    const rate =
      current.convertion_rate != null && current.convertion_rate !== ""
        ? Number(current.convertion_rate)
        : Number(detail?.convertion_rate) || 1;

    const nextRows = [...rows];
    const isInside = !!current.is_inside;
    nextRows[index] = {
      ...current,
      total_shipment: receiveQty,
      // total_balance is stored in base unit; it should update by conversion rate
      total_balance: Number((receiveQty * rate).toFixed(2)),
      // inside locations shipment qty mirrors receive qty, outside stays fixed
      shipment_qty_fixed: isInside ? receiveQty : current.shipment_qty_fixed,
    };

    syncTracks(nextRows);
  };

  const copyIssueQtyToNextRows = (fromIndex) => {
    const rows = form.getFieldValue("tracks") || [];
    const source = rows?.[fromIndex];
    if (!source) return;
    const copiedRows = [...rows];
    for (let i = fromIndex + 1; i < copiedRows.length; i++) {
      const rate =
        copiedRows[i].convertion_rate != null && copiedRows[i].convertion_rate !== ""
          ? Number(copiedRows[i].convertion_rate)
          : Number(detail?.convertion_rate) || 1;
      const isInside = !!copiedRows[i].is_inside;
      copiedRows[i] = {
        ...copiedRows[i],
        total_shipment: source.total_shipment,
        total_balance: Number((Number(source.total_shipment ?? 0) * rate).toFixed(2)),
        shipment_qty_fixed: isInside
          ? Number(source.total_shipment ?? 0)
          : copiedRows[i].shipment_qty_fixed,
      };
    }
    syncTracks(copiedRows);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!detail) return;

      setSaving(true);
      const locations =
        values.tracks?.map((row) => ({
          id: Number(row.history_id) || row.history_id,
          track_id: Number(row.track_id) || row.track_id,
          bin_id: Number(row.bin_id) || row.bin_id,
          shipment_qty: Number(row.total_shipment ?? 0),
          total_quantity: Number(row.total_balance ?? 0),
          convertion_unit_id: Number(row.convertion_unit_id) || detail.convertion_unit_id || detail.convertion_unit?.id || 1,
          convertion_rate:
            row.convertion_rate != null && row.convertion_rate !== ""
              ? Number(row.convertion_rate)
              : Number(detail.convertion_rate) || 1,
          is_delete: 0,
        })) ?? [];

      const bodyData = {
        movement_type: detail.movement_type,
        received_total_quantity: Number(values.received_total_quantity ?? 0),
        shipment_qty: Number(values.shipment_qty ?? 0),
        supplier_id: showSupplier ? values.supplier_id ?? null : null,
        invoice_id: showInvoice ? values.invoice_id ?? null : null,
        challan_id: showChallan ? values.challan_id ?? null : null,
        requisition_no: showRequisition ? values.requisition_no ?? null : null,
        reference_no: showReferenceNo ? values.reference_no ?? null : null,
        gate_pass: showGatePass ? values.gate_pass ?? null : null,
        entry_date: values.entry_date ? moment(values.entry_date).format("YYYY-MM-DD") : detail.entry_date,
        locations,
      };

      const response = await postData(`${RAW_MATERIAL_UPDATE}/${detail.id}`, bodyData);
      if (response && response?.code === 200) {
        HandleNotification("success", "bottomRight", "Updated successfully", null);
        onSaved?.();
        onClose();
      } else {
        HandleNotification("error", "bottomRight", response?.message?.[0] || "Update failed", null);
      }
    } catch (e) {
      if (e?.errorFields) return;
      HandleNotification("error", "bottomRight", "Something went wrong", null);
    } finally {
      setSaving(false);
    }
  };

  const { Option } = Select;

  return (
    <Modal
      title="Edit trims and accessories entry"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={saving}
      width={920}
      destroyOnClose
      maskClosable={false}
    >
      <Spin spinning={loading}>
        {detail && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              {detail.style_info?.style_no && <>Style: {detail.style_info.style_no} · </>}
              {detail.inventory_info?.item_info?.name && <>{detail.inventory_info.item_info.name}</>}
            </Text>
          </div>
        )}

        <Form form={form} layout="vertical" size="small">
          <Row gutter={12}>
            <Col xs={24} sm={8}>
              <Form.Item label="Date" name="entry_date" rules={[{ required: true, message: "Required" }]}>
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  disabledDate={(current) => current && current.endOf("day").isAfter(moment().endOf("day"))}
                />
              </Form.Item>
            </Col>
            {showSupplier && (
              <Col xs={24} sm={8}>
                <Form.Item label="Supplier" name="supplier_id" rules={[{ required: isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier, message: "Required" }]}>
                  <Select
                    allowClear
                    showSearch
                    placeholder="Supplier"
                    optionFilterProp="children"
                    onSearch={(v) => loadSuppliers(v)}
                    filterOption={false}
                    onChange={onSupplierChange}
                    disabled={supplierFieldsDisabled}
                  >
                    {supplierList.map((s) => (
                      <Option key={s.id} value={String(s.id)}>
                        {s.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            {showInvoice && (
              <Col xs={24} sm={8}>
                <Form.Item label="Invoice" name="invoice_id">
                  <Select allowClear showSearch placeholder="Invoice" optionFilterProp="children" disabled={supplierFieldsDisabled}>
                    {invoiceList.map((inv) => (
                      <Option key={inv.id} value={inv.id}>
                        {inv.invoice_number || inv.invoice_no || inv.number || inv.id}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            {showChallan && (
              <Col xs={24} sm={8}>
                <Form.Item label="Challan" name="challan_id">
                  <Select
                    allowClear
                    showSearch
                    placeholder="Challan"
                    optionFilterProp="children"
                    onSearch={(v) => loadChallans(v)}
                    filterOption={false}
                    disabled={supplierFieldsDisabled}
                  >
                    {challanList.map((c) => (
                      <Option key={c.id} value={c.id}>
                        {c.challan_no || c.name || c.id}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            {showRequisition && (
              <Col xs={24} sm={8}>
                <Form.Item label="Requisition No" name="requisition_no" rules={[{ required: requireRequisition, message: "Required" }]}>
                  {/* <StoreRequisitionNoSelect movementType={movementType} /> */}
                  <Input size="small" placeholder="Requisition No." />
                </Form.Item>
              </Col>
            )}
            {showReferenceNo && (
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Reference No"
                  name="reference_no"
                  rules={[{ required: isIssueToLab || isIssueToOthers || isSendToBuyingOffice, message: "Required" }]}
                >
                  <Input size="small" placeholder="Reference no." />
                </Form.Item>
              </Col>
            )}
            {showGatePass && (
              <Col xs={24} sm={8}>
                <Form.Item label="Gate Pass" name="gate_pass" rules={[{ required: true, message: "Required" }]}>
                  <Input size="small" placeholder="Gate pass" />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={8}>
              <Form.Item
                label={`${actionLabel} total quantity (BOM Unit)`}
                name="received_total_quantity"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  disabled
                  min={0}
                  formatter={(v) => (v === undefined || v === null ? "" : commaSeparateNumber(v))}
                  parser={(v) => (v ? String(v).replace(/,/g, "") : "")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label={`${actionLabel} qty`} name="shipment_qty" rules={[{ required: true, message: "Required" }]}>
                <InputNumber
                  style={{ width: "100%" }}
                  disabled
                  min={0}
                  formatter={(v) => (v === undefined || v === null ? "" : commaSeparateNumber(v))}
                  parser={(v) => (v ? String(v).replace(/,/g, "") : "")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Shipment unit" name="shipment_unit">
                <Input size="small" disabled placeholder="Auto populated" />
              </Form.Item>
            </Col>
          </Row>

          <Typography.Title level={5} style={{ marginTop: 8 }}>
            Location and Item Details
          </Typography.Title>

          {trackRows.length === 0 ? (
            <Text type="secondary">No location data for this entry.</Text>
          ) : (
            <div style={{ overflowX: "auto", marginBottom: 8 }}>
              <table style={{ width: "100%", minWidth: 980, borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {[
                      "Warehouse",
                      "Rack",
                      "Bin",
                      "Item Type",
                      "Item",
                      "Unit",
                      `${actionLabel} in BOM Unit`,
                      issueQtyLabel,
                    ].map(
                      (h) => (
                        <th key={h} style={{ border: "1px solid #d9d9d9", padding: "8px", textAlign: "center" }}>
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {trackRows.map((_, index) => (
                    <tr key={index}>
                      <td style={{ border: "1px solid #d9d9d9", padding: "6px" }}>
                        <Form.Item name={["tracks", index, "history_id"]} hidden><Input type="hidden" /></Form.Item>
                        <Form.Item name={["tracks", index, "track_id"]} hidden><Input type="hidden" /></Form.Item>
                        <Form.Item name={["tracks", index, "bin_id"]} hidden><Input type="hidden" /></Form.Item>
                        <Form.Item name={["tracks", index, "is_inside"]} hidden><Input type="hidden" /></Form.Item>
                        <Form.Item name={["tracks", index, "convertion_unit_id"]} hidden><Input type="hidden" /></Form.Item>
                        <Form.Item name={["tracks", index, "convertion_rate"]} hidden><Input type="hidden" /></Form.Item>
                        <Form.Item name={["tracks", index, "shipment_qty_fixed"]} hidden><Input type="hidden" /></Form.Item>
                        <Form.Item name={["tracks", index, "warehouse_label"]} style={{ marginBottom: 0 }}>
                          <Input size="small" disabled />
                        </Form.Item>
                      </td>
                      <td style={{ border: "1px solid #d9d9d9", padding: "6px" }}>
                        <Form.Item name={["tracks", index, "rack_label"]} style={{ marginBottom: 0 }}>
                          <Input size="small" disabled />
                        </Form.Item>
                      </td>
                      <td style={{ border: "1px solid #d9d9d9", padding: "6px" }}>
                        <Form.Item name={["tracks", index, "bin_label"]} style={{ marginBottom: 0 }}>
                          <Input size="small" disabled />
                        </Form.Item>
                      </td>
                      <td style={{ border: "1px solid #d9d9d9", padding: "6px", textAlign: "center" }}>
                        {detail?.inventory_info?.item_category_info?.name || "—"}
                      </td>
                      <td style={{ border: "1px solid #d9d9d9", padding: "6px", textAlign: "center" }}>
                        {detail?.inventory_info?.item_info?.name || "—"}
                      </td>
                      <td style={{ border: "1px solid #d9d9d9", padding: "6px", textAlign: "center" }}>
                        {detail?.inventory_info?.unit_info?.name || "—"}
                      </td>
                      <td style={{ border: "1px solid #d9d9d9", padding: "6px" }}>
                        <Form.Item name={["tracks", index, "total_balance"]} style={{ marginBottom: 0 }}>
                          <InputNumber
                            style={{ width: "100%" }}
                            disabled
                            formatter={(v) => (v === undefined || v === null ? "" : commaSeparateNumber(v))}
                          />
                        </Form.Item>
                      </td>
                      <td style={{ border: "1px solid #d9d9d9", padding: "6px" }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <Form.Item name={["tracks", index, "total_shipment"]} style={{ marginBottom: 0, width: "100%" }}>
                            <InputNumber
                              style={{ width: "100%" }}
                              min={0}
                              onChange={(v) => updateReceiveQty(index, v)}
                              formatter={(v) => (v === undefined || v === null ? "" : commaSeparateNumber(v))}
                              parser={(v) => (v ? String(v).replace(/,/g, "") : "")}
                            />
                          </Form.Item>
                          <CopyOutlined onClick={() => copyIssueQtyToNextRows(index)} style={{ cursor: "pointer" }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default TrimsStoreEntryEditModal;
